import { useEffect, useState } from "react";
import api from "../utils/api";
import { API_ENDPOINTS } from "../utils/urls";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

interface DashboardData {
  user: {
    name: string;
    email: string;
  };
  todaySales: {
    ordersCount: number;
    totalIncome: number;
  };
  lowStockItems: Array<{
    variantId: string;
    itemName: string;
    variantName: string;
    stock: number;
    price: number;
  }>;
}

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const getDashboardData = async () => {
    try {
      setLoading(true);
      const res = await api.get(API_ENDPOINTS.DASHBOARD);
      setDashboardData(res.data.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboardData();
  }, []);

  return (
    <div className="min-h-screen flex flex-col p-8">
      <Navbar />
      <main className="flex-grow px-6 py-10 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 text-gray-900">
            Welcome to Bill-lex Dashboard
          </h1>
          <p className="text-lg text-gray-600">
            Billing has never been so easy. Simplify your invoicing, inventory,
            and payments – all in one place.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* User Information */}
            <div className="bg-white border rounded-lg p-6 shadow-sm mb-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">Your Profile</h3>
              {dashboardData ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Name</p>
                    <p className="text-lg font-medium text-gray-900">{dashboardData.user.name}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Email</p>
                    <p className="text-lg font-medium text-gray-900">{dashboardData.user.email}</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Unable to load profile data</p>
              )}
            </div>

            {/* Sales Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm font-medium">
                      Today's Orders
                    </p>
                    <p className="text-3xl font-bold">{dashboardData?.todaySales.ordersCount || 0}</p>
                  </div>
                  <div className="bg-green-400 bg-opacity-30 rounded-full p-3">
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm font-medium">
                      Total Income Today
                    </p>
                    <p className="text-3xl font-bold">₹{dashboardData?.todaySales.totalIncome || 0}</p>
                  </div>
                  <div className="bg-blue-400 bg-opacity-30 rounded-full p-3">
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Stock Management Section */}
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6 shadow-sm mb-6">
              <div className="flex items-center mb-4">
                <div className="bg-red-100 rounded-full p-2 mr-3">
                  <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-red-800">Stock Management</h3>
              </div>
              
              {dashboardData?.lowStockItems && dashboardData.lowStockItems.length > 0 ? (
                <>
                  <p className="text-red-700 mb-4">
                    The following items are running low on stock (≤5 units remaining):
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dashboardData.lowStockItems.map((item) => (
                      <div key={item.variantId} className="bg-white border border-red-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-gray-900">{item.itemName}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item.stock === 0 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {item.stock === 0 ? 'Out of Stock' : `${item.stock} left`}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{item.variantName}</p>
                        <p className="text-sm font-medium text-gray-800">₹{item.price}</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="bg-green-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-green-700 font-medium mb-2">All items are well stocked!</p>
                  <p className="text-gray-600 text-sm">No items are running low on stock (≤5 units).</p>
                </div>
              )}
              
              <div className="mt-6 pt-4 border-t border-red-200">
                <button 
                  onClick={() => window.location.href = '/inventory'}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Manage Inventory
                </button>
              </div>
            </div>

            {/* Transparent Pricing */}
            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded shadow-sm">
              <h2 className="text-xl font-semibold text-blue-800 mb-1">
                Transparent Pricing
              </h2>
              <p className="text-blue-700">
                <strong>10% commission</strong> on payments (paid by merchants) and
                a <strong>2% platform fee</strong> (paid by users) ensure
                sustainable and seamless service.
              </p>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
